/**
 * RESEARCH CONTROL — THE SUBJECTS FIRST, THE WINDOWS BESIDE THEM.
 *
 * THE OWNER'S STRUCTURAL INSTRUCTION. A research WINDOW is an authorization: it
 * says Scout was allowed to work between two instants. A research TOPIC is the
 * subject that work is about. The old Control page led with the authorizations,
 * so the Owner could see that a window had run without being able to see what it
 * had been about. This page leads with the subjects and keeps the open windows
 * as a compact fact beside them.
 *
 * WHAT A ROW MAY CLAIM. Workspace, status, last activity and the section counts
 * are read from records. A count the backend could not project is rendered as
 * "not read", never as zero; a topic with no experiments yet is a real zero and
 * says so. The table never derives progress from how long a summary is.
 *
 * THE PAGE STILL OFFERS NO LAUNCHER. A window is authorized in the Main Chat
 * through the engine's own admission path; a form here would be a second way to
 * authorize unattended research, and the second way is the one that drifts.
 */

import { useEffect, useMemo, useState } from 'preact/hooks';
import { createLoadable } from '../resource';
import { readGateway } from '../gateway';
import { Failure } from './failure';
import { PageHeader, Technical } from './about';
import { navigate, route } from '../router';
import { formatTurnTime } from '../chat';
import { displayStateOf } from '../activity';

interface TopicStats {
	journalCount: number;
	knowledgeCount: number;
	episodeCount: number;
	sourceCount: number;
	experimentCount: number;
	hypothesisCount: number;
	improvementCount: number;
	openQuestionCount: number;
}

interface Topic {
	topicId: string;
	spaceId?: string | null;
	title: string;
	objective: string;
	summary: string;
	status: string;
	createdAt: string;
	updatedAt: string;
	lastActivityAt: string;
	stats?: TopicStats | null;
}

interface TopicsPayload {
	wired: boolean;
	reason?: string;
	topics: Topic[];
	hasMore?: boolean;
}

interface Space {
	spaceId: string;
	name: string;
	status: string;
}

interface SpacesPayload {
	ok: boolean;
	wired: boolean;
	reason?: string;
	spaces: Space[];
}

interface Run {
	runId: string;
	state: string;
	authorizationText: string;
	topicIntent?: string | null;
	subjectText?: string | null;
	deadlineAt: string;
	segmentsRun: number;
	cancelRequested: boolean;
	lastSegmentAt: string | null;
}

interface RunsPayload {
	wired: boolean;
	reason?: string;
	runs: Run[];
	generatedAt: string;
}

const STATUSES = ['ONGOING', 'UNRESOLVED', 'PROVISIONAL', 'CONCLUDED', 'SUSPENDED'] as const;

type SortKey = 'activity' | 'title' | 'status' | 'journal' | 'evidence' | 'questions';

function countText(value: number | undefined, stats: TopicStats | null | undefined): string {
	if (stats === null || stats === undefined) return '—';
	return String(value ?? 0);
}

function bySort(key: SortKey): (left: Topic, right: Topic) => number {
	switch (key) {
		case 'title':
			return (left, right) => left.title.localeCompare(right.title);
		case 'status':
			return (left, right) => left.status.localeCompare(right.status) || left.title.localeCompare(right.title);
		case 'journal':
			return (left, right) => (right.stats?.journalCount ?? 0) - (left.stats?.journalCount ?? 0);
		case 'evidence':
			return (left, right) => (right.stats?.sourceCount ?? 0) - (left.stats?.sourceCount ?? 0);
		case 'questions':
			return (left, right) =>
				(right.stats?.openQuestionCount ?? 0) - (left.stats?.openQuestionCount ?? 0);
		case 'activity':
		default:
			return (left, right) =>
				Date.parse(right.lastActivityAt) - Date.parse(left.lastActivityAt);
	}
}

export function ResearchControlView() {
	const topicsRead = useMemo(
		() => createLoadable<TopicsPayload>(() => readGateway<TopicsPayload>('/api/scout/gateway/research/topics?limit=100')),
		[],
	);
	const spacesRead = useMemo(
		() => createLoadable<SpacesPayload>(() => readGateway<SpacesPayload>('/api/scout/gateway/spaces?limit=200')),
		[],
	);
	const runsRead = useMemo(
		() => createLoadable<RunsPayload>(() => readGateway<RunsPayload>('/api/scout/gateway/research/runs')),
		[],
	);
	useEffect(() => {
		void topicsRead.load();
		void spacesRead.load();
		void runsRead.load();
	}, [topicsRead, spacesRead, runsRead]);

	const [query, setQuery] = useState('');
	const [workspace, setWorkspace] = useState('all');
	const [status, setStatus] = useState('all');
	const [sort, setSort] = useState<SortKey>('activity');

	const topicsState = topicsRead.state.value;
	const spacesState = spacesRead.state.value;
	const runsState = runsRead.state.value;

	if (topicsState.status === 'failed') {
		return (
			<section class="view">
				<PageHeader id="research-control-heading" title="Research Control" about={ABOUT} />
				{topicsState.error !== null && (
					<Failure
						error={topicsState.error}
						what="the research directory"
						onRetry={() => void topicsRead.load()}
					/>
				)}
			</section>
		);
	}

	const spaces = spacesState.data?.spaces ?? [];
	const spaceName = (spaceId: string | null | undefined): string => {
		if (spaceId === null || spaceId === undefined) return 'Main Scout';
		return spaces.find((space) => space.spaceId === spaceId)?.name ?? spaceId;
	};
	const allTopics = topicsState.data?.topics ?? [];
	const filtered = allTopics
		.filter((topic) => {
			const wanted = query.trim().toLowerCase();
			if (
				wanted.length > 0 &&
				!`${topic.title} ${topic.objective} ${topic.summary}`.toLowerCase().includes(wanted)
			) {
				return false;
			}
			if (workspace === 'main' && (topic.spaceId ?? null) !== null) return false;
			if (workspace !== 'all' && workspace !== 'main' && topic.spaceId !== workspace) return false;
			if (status !== 'all' && topic.status !== status) return false;
			return true;
		})
		.sort(bySort(sort));

	const openRuns = (runsState.data?.runs ?? []).filter((run) => {
		const display = displayStateOf(
			{
				state: run.state,
				deadlineAt: run.deadlineAt,
				cancelRequested: run.cancelRequested,
				lastSegmentAt: run.lastSegmentAt,
			},
			Date.parse(runsState.data?.generatedAt ?? new Date().toISOString()),
		);
		return display === 'RUNNING' || display === 'CANCELLING' || display === 'DEADLINE_PASSED_UNOBSERVED';
	});
	const openQuestions = allTopics.reduce(
		(total, topic) => total + (topic.stats?.openQuestionCount ?? 0),
		0,
	);
	const activeWorkspaces = spaces.filter((space) => space.status === 'ACTIVE').length;
	const loading = topicsState.status === 'loading' && topicsState.data === null;

	return (
		<section class="view research-control">
			<PageHeader id="research-control-heading" title="Research Control" about={ABOUT}>
				<p class="view-blurb">
					Every subject Scout is investigating, with the workspace that owns it, what its
					records hold, and what is open. Research windows are authorizations; the subject
					is the thing worth reading.
				</p>
			</PageHeader>

			<div class="stat-grid" aria-label="Research at a glance">
				<div class="stat">
					<span class="stat-label">Research topics</span>
					<span class="stat-value">{loading ? '—' : allTopics.length}</span>
					<span class="stat-note">{allTopics.filter((topic) => topic.status === 'ONGOING').length} ongoing</span>
				</div>
				<div class="stat">
					<span class="stat-label">Open windows</span>
					<span class="stat-value">{openRuns.length}</span>
					<span class="stat-note">authorized and not yet ended</span>
				</div>
				<div class="stat">
					<span class="stat-label">Open questions</span>
					<span class="stat-value">{openQuestions}</span>
					<span class="stat-note">linked to a topic</span>
				</div>
				<div class="stat">
					<span class="stat-label">Research workspaces</span>
					<span class="stat-value">{spaces.length}</span>
					<span class="stat-note">{activeWorkspaces} active</span>
				</div>
			</div>

			<section class="panel panel-flush" aria-labelledby="topics-heading">
				<div class="section-head">
					<div>
						<h2 id="topics-heading">Research topics</h2>
						<p class="section-note">
							{loading
								? 'Reading the research directory…'
								: `${filtered.length} of ${allTopics.length} topics`}
						</p>
					</div>
					<Technical summary="What a topic is">
						<p>
							A topic is a continuing subject Scout returns to across windows. Opening one
							shows its journal, knowledge, experiments, evidence, hypotheses, improvements
							and open questions as separate tables.
						</p>
					</Technical>
				</div>

				<div class="toolbar" role="search">
					<label class="toolbar-field toolbar-field--grow">
						<span class="visually-hidden">Search topics</span>
						<input
							class="toolbar-input"
							type="search"
							placeholder="Search topic, objective or summary…"
							value={query}
							onInput={(event) => setQuery((event.target as HTMLInputElement).value)}
						/>
					</label>
					<label class="toolbar-field">
						<span class="toolbar-label">Workspace</span>
						<select
							class="toolbar-select"
							value={workspace}
							onChange={(event) => setWorkspace((event.target as HTMLSelectElement).value)}
						>
							<option value="all">All workspaces</option>
							<option value="main">Main Scout</option>
							{spaces.map((space) => (
								<option key={space.spaceId} value={space.spaceId}>
									{space.name}
								</option>
							))}
						</select>
					</label>
					<label class="toolbar-field">
						<span class="toolbar-label">Status</span>
						<select
							class="toolbar-select"
							value={status}
							onChange={(event) => setStatus((event.target as HTMLSelectElement).value)}
						>
							<option value="all">All statuses</option>
							{STATUSES.map((name) => (
								<option key={name} value={name}>
									{name}
								</option>
							))}
						</select>
					</label>
					{(query.length > 0 || workspace !== 'all' || status !== 'all') && (
						<button
							type="button"
							class="button ghost small"
							onClick={() => {
								setQuery('');
								setWorkspace('all');
								setStatus('all');
							}}
						>
							Clear
						</button>
					)}
				</div>

				{loading && <p class="notice" role="status">Reading the research directory…</p>}
				{topicsState.data?.wired === false && (
					<p class="notice">
						{topicsState.data.reason ??
							'This deployment has no research-topic store. That is a deployment gap, not evidence that no subject was researched.'}
					</p>
				)}
				{!loading && topicsState.data?.wired !== false && filtered.length === 0 && (
					<div class="empty-state">
						<p class="empty-title">
							{allTopics.length === 0 ? 'No research topics on record' : 'No topics match these filters'}
						</p>
						<p class="empty-note">
							{allTopics.length === 0
								? 'A topic is created when Scout opens a subject in a research window. Until then there is nothing to list — this is an empty directory, not a failed read.'
								: 'Change the search or clear the workspace and status filters.'}
						</p>
					</div>
				)}

				{filtered.length > 0 && (
					<div class="table-wrap">
						<table class="data-table topic-directory">
							<thead>
								<tr>
									<th scope="col" aria-sort={sort === 'title' ? 'ascending' : 'none'}>
										<button type="button" class="th-sort" onClick={() => setSort('title')}>
											Research topic
										</button>
									</th>
									<th scope="col">Workspace</th>
									<th scope="col" aria-sort={sort === 'status' ? 'ascending' : 'none'}>
										<button type="button" class="th-sort" onClick={() => setSort('status')}>
											Status
										</button>
									</th>
									<th scope="col" aria-sort={sort === 'activity' ? 'descending' : 'none'}>
										<button type="button" class="th-sort" onClick={() => setSort('activity')}>
											Last activity
										</button>
									</th>
									<th scope="col" class="numeric" aria-sort={sort === 'journal' ? 'descending' : 'none'}>
										<button type="button" class="th-sort" onClick={() => setSort('journal')}>
											Journal
										</button>
									</th>
									<th scope="col" class="numeric" aria-sort={sort === 'evidence' ? 'descending' : 'none'}>
										<button type="button" class="th-sort" onClick={() => setSort('evidence')}>
											Evidence
										</button>
									</th>
									<th
										scope="col"
										class="numeric"
										aria-sort={sort === 'questions' ? 'descending' : 'none'}
									>
										<button type="button" class="th-sort" onClick={() => setSort('questions')}>
											Open questions
										</button>
									</th>
									<th scope="col" class="cell-action">
										<span class="visually-hidden">Open</span>
									</th>
								</tr>
							</thead>
							<tbody>
								{filtered.map((topic) => {
									const href = `/research/topic/${encodeURIComponent(topic.topicId)}`;
									return (
										<tr
											key={topic.topicId}
											class="row-link"
											onClick={(event) => {
												if ((event.target as HTMLElement).closest('a, button') !== null) return;
												navigate({ destination: 'research', section: 'topic', recordId: topic.topicId });
											}}
										>
											<td class="cell-title">
												<a
													href={href}
													onClick={(event) => {
														if (
															event.metaKey ||
															event.ctrlKey ||
															event.shiftKey ||
															event.altKey ||
															event.button !== 0
														) {
															return;
														}
														event.preventDefault();
														navigate({ destination: 'research', section: 'topic', recordId: topic.topicId });
													}}
												>
													{topic.title}
												</a>
												<span class="cell-sub">{topic.summary || topic.objective}</span>
											</td>
											<td class="cell-muted" data-label="Workspace">
												{topic.spaceId === null || topic.spaceId === undefined ? (
													'Main Scout'
												) : (
													<a
														href={`/spaces/${encodeURIComponent(topic.spaceId)}`}
														onClick={(event) => {
															if (
																event.metaKey ||
																event.ctrlKey ||
																event.shiftKey ||
																event.altKey ||
																event.button !== 0
															) {
																return;
															}
															event.preventDefault();
															navigate({ destination: 'spaces', section: topic.spaceId ?? null, recordId: null });
														}}
													>
														{spaceName(topic.spaceId)}
													</a>
												)}
											</td>
											<td data-label="Status">
												<span class={`status status-${topic.status.toLowerCase()}`}>{topic.status}</span>
											</td>
											<td class="cell-date" data-label="Last activity">
												{formatTurnTime(topic.lastActivityAt)}
											</td>
											<td class="numeric" data-label="Journal">
												{countText(topic.stats?.journalCount, topic.stats)}
											</td>
											<td class="numeric" data-label="Evidence">
												{countText(topic.stats?.sourceCount, topic.stats)}
											</td>
											<td class="numeric" data-label="Open questions">
												{countText(topic.stats?.openQuestionCount, topic.stats)}
											</td>
											<td class="cell-action">
												<a
													class="button ghost small"
													href={href}
													onClick={(event) => {
														if (
															event.metaKey ||
															event.ctrlKey ||
															event.shiftKey ||
															event.altKey ||
															event.button !== 0
														) {
															return;
														}
														event.preventDefault();
														navigate({ destination: 'research', section: 'topic', recordId: topic.topicId });
													}}
												>
													Open
												</a>
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				)}
			</section>

			<div class="dashboard-grid dashboard-grid--wide">
				<section class="panel" aria-labelledby="attention-heading">
					<div class="section-head">
						<h2 id="attention-heading">What needs attention</h2>
						<a class="link-button" href="/research/windows">
							Every window
						</a>
					</div>
					{openRuns.length === 0 ? (
						<p class="empty-note">
							No research window is open. A window is authorized by the Owner in the Main
							Chat; nothing here starts one.
						</p>
					) : (
						<ul class="attention-list">
							{openRuns.slice(0, 6).map((run) => {
								const display = displayStateOf(
									{
										state: run.state,
										deadlineAt: run.deadlineAt,
										cancelRequested: run.cancelRequested,
										lastSegmentAt: run.lastSegmentAt,
									},
									Date.parse(runsState.data?.generatedAt ?? new Date().toISOString()),
								);
								return (
									<li key={run.runId} class="attention-row">
										<div>
											<span class={`status status-${display.toLowerCase()}`}>{display.replaceAll('_', ' ')}</span>
											<span class="attention-subject">
												{run.subjectText ?? run.topicIntent ?? run.authorizationText}
											</span>
										</div>
										<div class="attention-facts">
											<span>deadline {formatTurnTime(run.deadlineAt)}</span>
											<span>{run.segmentsRun} segments run</span>
										</div>
									</li>
								);
							})}
						</ul>
					)}
				</section>

				<section class="panel" aria-labelledby="coverage-heading">
					<div class="section-head">
						<h2 id="coverage-heading">Coverage</h2>
					</div>
					<dl class="facts facts-compact">
						<dt>Journal entries</dt>
						<dd>{allTopics.reduce((total, topic) => total + (topic.stats?.journalCount ?? 0), 0)}</dd>
						<dt>Knowledge entries</dt>
						<dd>{allTopics.reduce((total, topic) => total + (topic.stats?.knowledgeCount ?? 0), 0)}</dd>
						<dt>Evidence sources</dt>
						<dd>{allTopics.reduce((total, topic) => total + (topic.stats?.sourceCount ?? 0), 0)}</dd>
						<dt>Experiments</dt>
						<dd>{allTopics.reduce((total, topic) => total + (topic.stats?.experimentCount ?? 0), 0)}</dd>
					</dl>
					<p class="section-note">
						Counts are records linked to a topic. A record without a topic link is not
						counted here and is not shown as if it were; the paper trail is on the topic
						page, section by section.
					</p>
					<p class="section-note">
						{topicsState.data?.hasMore === true
							? 'More topics exist than this page loaded. Narrow by workspace or search to reach them.'
							: 'This directory is complete for the owner.'}
					</p>
				</section>
			</div>
		</section>
	);
}

const ABOUT = {
	purpose: (
		<>
			This is the research directory: the subjects Scout is investigating, their workspace,
			their status, and how much evidence each one holds. Opening a subject shows its seven
			research tables on one page.
		</>
	),
	how: (
		<>
			Search or filter the directory, then open a topic. The Windows tab carries every
			authorization and its execution facts.
		</>
	),
	limits: (
		<>
			<p>
				There is no start button here. A research window is authorized by the Owner in the
				Main Chat, through the engine's own admission path; this page reads and cancels.
			</p>
			<p>
				A count of zero means no record of that kind is linked to the topic. A dash means
				the deployment could not project the count — those are different statements.
			</p>
		</>
	),
};
